import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"

interface Client {
	id: string
	logo: string
	name: string
	testimonial: string
	role: string
}

const clients: Client[] = [
	{
		id: "rogrowth",
		logo: "/clients/rogrowth.webp",
		name: "RoGrowth",
		testimonial: "RoGrowth has collaborated with LionOG a couple times, fortnite vids + Roblox, I would say he's reliable and is the only other consistent agency out there 😎 Would recommend",
		role: "Chrollo",
	},
	{
		id: "endpixel",
		logo: "/clients/endpixel.png",
		name: "EndPixel",
		testimonial: "LionOG Provides an amazing experience; there is a reason he has been successful over and over again in pushing your business or brand to its fullest potential. He allows anyone to build an audience and target the right demographic, and if things are not working out, he will work tirelessly with you to build things the right way. +1",
		role: "Kintsugi",
	},
	{
		id: "voldex",
		logo: "/clients/voldex.svg",
		name: "Voldex",
		testimonial: "With LionOG's dedication and a knack for innovation, LionOG has played an instrumental role in bringing our TikTok channel, @zosamuraigame, to new heights. LionOG's content has increased our following and has helped engage our enthusiastic community. Thanks again for your collaboration LionOG! Here at Voldex we value your partnership!",
		role: "Harry Bienenstock",
	},
]

interface LogoWithTestimonialProps {
	client: Client
	activeClient: Client | null
	setActiveClient: (client: Client | null) => void
}

function LogoWithTestimonial({ client, activeClient, setActiveClient }: LogoWithTestimonialProps) {
	const isActive = activeClient?.id === client.id

	return (
		<motion.span 
			className="relative inline-flex items-center mx-1 md:mx-2"
			onMouseEnter={() => setActiveClient(client)}
			onMouseLeave={() => setActiveClient(null)}
			whileHover={{ scale: 1.1 }}
			transition={{ duration: 0.2 }}
		>
			<img
				src={client.logo}
				alt={client.name}
				width={38}
				height={38}
				loading="eager"
				decoding="async"
				className={`h-[0.8em] w-auto rounded-md object-contain cursor-pointer transition-all duration-300 translate-y-[0.05em] ${
					isActive ? "brightness-125 drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]" : "hover:brightness-110 hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.4)]"
				}`}
			/>
			
			<AnimatePresence>
				{isActive && (
					<motion.div
						initial={{ opacity: 0, y: 10, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: 10, scale: 0.95 }}
						transition={{ duration: 0.2 }}
						className="fixed md:absolute z-50 md:top-full md:mt-4 w-[280px] max-w-[90vw] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 md:translate-y-0"
					>
						<div className="bg-[#1a1a1a] text-white p-4 rounded-xl shadow-2xl border border-pink/30">
							<p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-3 md:mb-4">
								"{client.testimonial}"
							</p>
							<div className="flex items-center gap-2 md:gap-3">
								<img
									src={client.logo}
									alt={client.name}
									width={40}
									height={40}
									loading="lazy"
									decoding="async"
									className="w-8 h-8 md:w-10 md:h-10 rounded-lg object-contain bg-white/10 p-1"
								/>
								<div>
									<p className="font-semibold text-pink text-xs md:text-sm">{client.name}</p>
									<p className="text-gray-400 text-[10px] md:text-xs">{client.role}</p>
								</div>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</motion.span>
	)
}

export default function ClientTestimonials() {
	const [activeClient, setActiveClient] = useState<Client | null>(null)

	return (
		<div className="bg-navy text-white w-full 2xl:px-52 px-0 py-16 md:py-24 lg:py-32">
			<div className="lg:px-24 px-6 md:px-8 flex flex-col gap-6 md:gap-8">
				{/* Header text with inline logos */}
				<motion.div 
					className="flex flex-col gap-3 md:gap-4"
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6 }}
				>
					<motion.p 
						className="text-pink text-xs md:text-sm uppercase tracking-wider font-medium"
						initial={{ opacity: 0, x: -20 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true, amount: 0.5 }}
						transition={{ duration: 0.5, delay: 0.1 }}
					>
						Hear from our clients
					</motion.p>
					
					<motion.h2 
						className="font-semibold text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl leading-[1.6] md:leading-[1.7]"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.2 }}
					>
						<span>We've scaled </span>
						<LogoWithTestimonial 
							client={clients[0]} 
							activeClient={activeClient} 
							setActiveClient={setActiveClient} 
						/>
						<span> over 25 games </span>
						<LogoWithTestimonial 
							client={clients[2]} 
							activeClient={activeClient} 
							setActiveClient={setActiveClient} 
						/>
						<span> to massive playerbases. </span>
						<LogoWithTestimonial 
							client={clients[1]} 
							activeClient={activeClient} 
							setActiveClient={setActiveClient} 
						/>
					</motion.h2>
					
					<motion.h2 
						className="font-semibold text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl mt-4 md:mt-8"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.5 }}
						transition={{ duration: 0.6, delay: 0.4 }}
					>
						Now we can do it for you.
					</motion.h2>

					{/* Hover hint */}
					<motion.p 
						className="text-muted text-xs md:text-sm mt-4 flex items-center gap-2"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true, amount: 0.5 }}
						transition={{ duration: 0.5, delay: 0.6 }}
					>
						<span className="inline-block w-4 h-4 rounded-full bg-pink/20 animate-pulse" />
						<span className="hidden sm:inline">Hover over the logos to see testimonials</span>
						<span className="sm:hidden">Tap logos for testimonials</span>
					</motion.p>
				</motion.div>
			</div>
		</div>
	)
}